import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { MarketConfigService } from 'src/app/core/services/market-config/market-config.service';
import { Vehicle } from 'src/app/core/vehicle/vehicle.interface';
import { arrayIntersect } from 'src/app/utils/functions/array-intersect';
import { ObjectStorage } from 'src/app/utils/object-storage/object-storage';

export interface VehiclePropertyFootnotesConfig {
  footnote: string;
  properties: string[];
  values: any[];
}

/**
 * The FootnoteHintsService retrieves footnote hints for respective data.
 */
@Injectable({
  providedIn: 'root'
})
export class FootnoteHintsService {
  public readonly globalContext = 'vehiclePropertyFootnotes';

  private readonly footnoteHintsSubject = new BehaviorSubject<Map<string, VehiclePropertyFootnotesConfig[]>>(
    new Map<string, VehiclePropertyFootnotesConfig[]>()
  );

  public constructor(private readonly marketConfigService: MarketConfigService) {
    this.marketConfigService
      .observe<VehiclePropertyFootnotesConfig[]>(this.globalContext)
      .subscribe(footnotesConfig => this.registerFootnoteHintConfig(this.globalContext, footnotesConfig));
  }

  /**
   * returns array of footnote hints for given property paths of vehicle object.
   * vehicle is needed as footnote configs may require specific vehicle property values.
   */
  public getFootnoteHintsForVehicleProperties(
    vehicle: Vehicle | ObjectStorage<Vehicle>,
    context: string = this.globalContext,
    ...propertyPaths: string[]
  ): Observable<string[]> {
    return this.footnoteHintsSubject.pipe(
      map(footnotesConfig => {
        const contextFootnotesConfig = footnotesConfig.get(context) || [];

        // reduce property footnotes config to footnote hint collection
        const footnoteHints = contextFootnotesConfig.reduce<Set<string>>((hints, footnoteConfig) => {
          // get intersection of property paths for this footnote and given property paths
          const pathMatches = arrayIntersect(footnoteConfig.properties || [], propertyPaths);

          // if there are matches for this footnote
          if (!!pathMatches.length) {
            hints = this.addFootnoteToHints(hints, vehicle, pathMatches, footnoteConfig);
          }

          return hints;
        }, new Set());

        // return hints collection as array
        return Array.from(footnoteHints.values());
      })
    );
  }

  public registerFootnoteHintConfig(context: string, config: VehiclePropertyFootnotesConfig[]): void {
    const footnoteHints = this.footnoteHintsSubject.getValue();
    footnoteHints.set(context, config);
    this.footnoteHintsSubject.next(footnoteHints);
  }

  public getVehiclePropertyFootnoteHints(key: string, footnoteConfigValue: string): Observable<string[]> {
    return this.footnoteHintsSubject.pipe(
      map(
        (
          vehiclePropertyFootnotesConfigs: Map<string, VehiclePropertyFootnotesConfig[]>
        ): VehiclePropertyFootnotesConfig[] => vehiclePropertyFootnotesConfigs.get(this.globalContext)
      ),
      map((footnoteConfigs: VehiclePropertyFootnotesConfig[]): string[] =>
        footnoteConfigs.reduce((accumulator: string[], current: VehiclePropertyFootnotesConfig): string[] => {
          // example: footnoteConfigValue: "118 kW + 75 kW"; config.values: ['kW +'] -> matches, therefore add footnote
          if (current.values.length) {
            current.values.forEach(value => {
              if (footnoteConfigValue?.includes(value)) {
                accumulator.push(current.footnote);
              }
            });
          } else if (current.properties.includes(key)) {
            accumulator.push(current.footnote);
          }
          return accumulator;
        }, [])
      ),
      map(arr => [...new Set(arr)]) // for removing duplicates
    );
  }

  private addFootnoteToHints(
    hints: Set<string>,
    vehicle: Vehicle | ObjectStorage<Vehicle>,
    pathMatches: string[],
    footnoteConfig: VehiclePropertyFootnotesConfig
  ): Set<string> {
    const vehicleObj = vehicle instanceof ObjectStorage ? vehicle : new ObjectStorage<Vehicle>(vehicle);

    // if this footnote requires a specific value for the property path
    if (footnoteConfig.values && !!footnoteConfig.values.length) {
      // if the vehicles property's value matches, add it to collection
      if (
        pathMatches.some(
          path =>
            // example: value: "80 kW (109 PS)"; config: "PS" -> matches, therefore add footnote™
            !!vehicleObj.get(path) && vehicleObj.get(path).includes(footnoteConfig.values)
        )
      ) {
        hints.add(footnoteConfig.footnote);
        return hints;
      }
    } else {
      // if this footnote is not bound to property values, simply add it to collection
      hints.add(footnoteConfig.footnote);
      return hints;
    }

    return hints;
  }
}
